import {
	Drawer,
	type DrawerProps,
	IconButton,
	Stack,
	type SxProps,
	type Theme,
	Typography,
	useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import type { FC, PropsWithChildren, ReactNode, SyntheticEvent } from "react";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { DrawerGrabber } from "web_myplann/Shared/Components/DrawerGrabber";
import { useCloseOnBackPress } from "web_myplann/Shared/Hooks/useCloseOnBackPress";
import { useHideNavbar } from "web_myplann/Shared/Hooks/useHideNavbar";
import { useSoftKeyboardOpen } from "web_myplann/Shared/Hooks/useSoftKeyboardOpen";
import { useSwipeToClose } from "web_myplann/Shared/Hooks/useSwipeToClose";

type TitleHeaderProps = {
	title?: string;
	closeButton?: boolean;
	header?: never;
};

type CustomHeaderProps = {
	header: ReactNode;
	title?: never;
	closeButton?: never;
};

export type MyPlannDrawerProps = DrawerProps & {
	/**
	 * Defaults to true — the grabber is the standard affordance of the new design.
	 * Only the legacy (pre-redesign) portfolio drawers opt out with `grabber={false}`,
	 * and AdvisorPickerDrawer / LanguageDrawer gate it on the pilot right because they
	 * render in both designs. FormSheet opts out because its custom header carries
	 * its own Cancel / Save actions.
	 *
	 * TODO: drop this prop once the legacy portfolio drawers are gone and the pilot
	 * right is rolled out, and render the grabber unconditionally.
	 */
	grabber?: boolean;
	disableBackdropClick?: boolean;
	concealed?: boolean;
	paperSx?: SxProps<Theme>;
} & (TitleHeaderProps | CustomHeaderProps);

export const MyPlannDrawer: FC<PropsWithChildren<MyPlannDrawerProps>> = ({
	anchor = "bottom",
	title,
	header,
	closeButton,
	grabber = true,
	disableBackdropClick,
	concealed,
	paperSx,
	children,
	onClose,
	open,
	...rest
}) => {
	const { palette, iconSize, spacingTokens } = useTheme();
	const hasGoalsRight = useGoalsRightOptional();

	const drawerOpen = useHideNavbar(open === true);
	const isSoftKeyboardOpen = useSoftKeyboardOpen(open === true);

	const dismiss = () => onClose?.({}, "escapeKeyDown");
	const swipeToClose = useSwipeToClose(dismiss, anchor === "bottom");
	useCloseOnBackPress(
		open === true,
		disableBackdropClick ? undefined : dismiss,
	);

	return (
		<Drawer
			anchor={anchor}
			open={drawerOpen}
			ModalProps={{
				sx: {
					zIndex: (t) => t.zIndex.drawer + 1,
					visibility: concealed ? "hidden" : undefined,
				},
			}}
			slotProps={{
				backdrop: { onClick: (e) => e.stopPropagation() },
				paper: {
					sx: [
						{
							paddingBottom: isSoftKeyboardOpen
								? 0
								: "min(var(--mobile-safe-area-bottom, env(safe-area-inset-bottom, 0px)), 10px)",
						},
						...(hasGoalsRight
							? [{ backgroundColor: palette.background.paper }]
							: []),
						...(Array.isArray(paperSx) ? paperSx : paperSx ? [paperSx] : []),
					],
					onClick: (e: SyntheticEvent) => {
						e.stopPropagation();
					},
				},
			}}
			onClose={(event, reason) => {
				if (reason === "backdropClick" && disableBackdropClick) {
					return;
				}
				if (onClose) onClose(event, reason);
			}}
			{...rest}
		>
			{grabber && <DrawerGrabber {...swipeToClose} />}
			{header ?? (
				<Stack
					pt={grabber ? spacingTokens.xs : spacingTokens.md}
					pb={hasGoalsRight ? spacingTokens.xs : spacingTokens.sm}
					px={spacingTokens.lg}
					direction={"row"}
					alignItems={"center"}
					justifyContent={"space-between"}
					{...swipeToClose}
					sx={{ touchAction: "none" }}
				>
					<Typography
						display="flex"
						alignItems="flex-start"
						variant={hasGoalsRight ? "h4" : "deprecatedH3"}
					>
						{title}
					</Typography>

					{closeButton && (
						<IconButton
							onClick={(e) => {
								onClose?.(e, "escapeKeyDown");
							}}
							sx={{
								bgcolor: palette.grey[100],
							}}
						>
							<X size={iconSize.sm} color={palette.grey[500]} />
						</IconButton>
					)}
				</Stack>
			)}
			<Stack flex="1 1 auto" sx={{ overflowY: "auto" }}>
				{children}
			</Stack>
		</Drawer>
	);
};
